/**
 * useAuthenticatedImage — scarica un'immagine tramite axiosInstance (con token JWT)
 * e restituisce un blob: URL sicuro da usare come src di <img>.
 *
 * NORMALIZZAZIONE URL (scelta A)
 * Il backend restituisce thumbnail_url e google_drive_url già prefissati con
 * l'intero percorso "/api/v1/…". axiosInstance ha però baseURL="/api/v1", quindi
 * passare quegli URL direttamente produrrebbe /api/v1/api/v1/… → 404.
 * toAxiosPath() rimuove il prefisso prima che axios lo usi, ovunque l'hook
 * venga chiamato, senza toccare il contratto dell'API né i chiamanti esistenti.
 *
 * VALIDAZIONE Content-Type
 * Se la risposta non è image/* (es. corpo JSON di un 4xx wrappato in 2xx dallo
 * stream) l'hook segnala errore senza creare un blob — impedisce l'icona rotta
 * silenziosa che aveva reso il difetto poco leggibile.
 *
 * REVOCA BLOB
 * Il blob viene REVOCATO automaticamente:
 *   - quando il componente che usa l'hook si smonta
 *   - quando `url` cambia (il blob precedente viene liberato prima del fetch successivo)
 * Questo previene perdite di memoria (test T12 del design foto).
 *
 * Uso:
 *   const { src, loading, error } = useAuthenticatedImage(photo.thumbnail_url)
 *   if (loading) return <Spin />
 *   if (error || !src) return <span>⚠️</span>
 *   return <img src={src} />
 */

import { useState, useEffect } from 'react'
import axiosInstance from '@/lib/axios'
import { API_URL } from '@/config/api'

export interface UseAuthenticatedImageResult {
  /** blob: URL pronto da passare a <img src>. null mentre carica o in caso di errore. */
  src: string | null
  /** true dal momento in cui parte la richiesta fino alla creazione del blob URL */
  loading: boolean
  /** true se la richiesta HTTP è fallita o la risposta non era un'immagine */
  error: boolean
}

/**
 * Rimuove il prefisso baseURL dall'URL se presente, così axiosInstance non lo
 * aggiunge una seconda volta.
 * Es. "/api/v1/work-orders/108/photos/6/image" → "/work-orders/108/photos/6/image"
 */
function toAxiosPath(url: string): string {
  if (API_URL && url.startsWith(API_URL)) {
    return url.slice(API_URL.length)
  }
  return url
}

export function useAuthenticatedImage(
  url: string | null | undefined,
): UseAuthenticatedImageResult {
  const [src, setSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!url) {
      setSrc(null)
      setLoading(false)
      setError(false)
      return
    }

    let blobUrl: string | null = null
    let cancelled = false

    setLoading(true)
    setError(false)
    setSrc(null)

    const axiosPath = toAxiosPath(url)

    axiosInstance
      .get<Blob>(axiosPath, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return

        // Validazione Content-Type: rifiuta corpi non-immagine (es. JSON di errore
        // che alcune configurazioni proxy consegnano con status 200).
        const contentType = (res.headers['content-type'] as string | undefined) ?? ''
        if (!contentType.startsWith('image/')) {
          setError(true)
          setLoading(false)
          return
        }

        blobUrl = URL.createObjectURL(res.data)
        setSrc(blobUrl)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      })

    // Cleanup: cancella il fetch in volo e revoca il blob se già creato.
    // Viene chiamato sia all'unmount sia a ogni cambio di `url`.
    return () => {
      cancelled = true
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
        blobUrl = null
      }
    }
  }, [url])

  return { src, loading, error }
}
